import { validateChatAnswer } from '../components/chat/services/chatValidationService'
import { normalizeValidationResult } from '../components/chat/utils/chatValidationHelpers'
import { CHAT_MESSAGES } from '../components/chat/config/chatMessages'
import { getLocalizedProductText } from '../components/chat/utils/productHelpers'

const useChatValidation = ({
  collectedAnswersRef,
  attemptCounts,
  validationState,
  dispatch,
  setValidationState,
  setAttemptCounts,
  getAnswersMap,
  saveAnswer,
  addAiMessage,
  goToNextStep,
  translations = {},
  language = 'EN',
}) => {
  const extractContextFromAnswer = async (step, answerText) => {
    try {
      const previousAttempts = collectedAnswersRef.current
        .filter((answer) => answer.stepId === step.id)
        .map((answer) => answer.text)
        .filter(Boolean)

      const currentAttempt = attemptCounts[step.id] || 1
      const previousData = validationState[step.id]?.extractedData || {}

      const result = await validateChatAnswer({
        endpoint: step.validationApi,
        userAnswer: answerText,
        attemptNumber: currentAttempt,
        previousAnswers: previousAttempts,
        previousData,
        language,
      })

      dispatch(
        setValidationState({
          ...validationState,
          [step.id]: {
            extractedData: result.extractedData || {},
            canonicalData: result.canonicalData || {},
            missingFields: result.missingFields || [],
            clarificationQuestion: result.clarificationQuestion || null,
          },
        }),
      )

      return result
    } catch (error) {
      return {
        isComplete: false,
        extractedData: {},
        canonicalData: {},
        missingFields: [],
        clarificationQuestion: getLocalizedProductText({
          translations,
          language,
          keyPath: 'chatMessages.validationFailed',
          fallback: CHAT_MESSAGES.validationFailed,
        }),
      }
    }
  }

  const handleValidatedStep = async (step, answerText, photoUrl = null) => {
    const currentAttempt = attemptCounts[step.id] || 1
    const rawResult = await extractContextFromAnswer(step, answerText)
    const llmResult = normalizeValidationResult(rawResult)

    if (!llmResult.isComplete) {
      if (currentAttempt >= (step.maxRetries || 3)) {
        saveAnswer({
          step,
          text: answerText,
          photoUrl,
          extractedValue: llmResult.extractedValue,
          value: llmResult.extractedValue,
          isValid: false,
          attempt: currentAttempt,
          meta: {
            canonicalData: llmResult.canonicalValue || {},
          },
        })

        dispatch(
          setAttemptCounts({
            ...attemptCounts,
            [step.id]: 1,
          }),
        )

        goToNextStep(step.id)
      } else {
        const nextAttempt = currentAttempt + 1

        dispatch(
          setAttemptCounts({
            ...attemptCounts,
            [step.id]: nextAttempt,
          }),
        )

        addAiMessage({
          text:
            llmResult.clarificationQuestion ||
            getLocalizedProductText({
              translations,
              language,
              keyPath: 'chatMessages.validationClarificationFallback',
              fallback: CHAT_MESSAGES.validationClarificationFallback,
            }),
        })
      }

      return
    }

    let finalExtractedValue = llmResult.extractedValue

    if (step.id === 'medical_lifestyle') {
      const answersMap = getAnswersMap()

      const gender = String(
        answersMap.demographics?.gender || answersMap.gender || '',
      )
        .trim()
        .toLowerCase()

      if (gender === 'male') {
        finalExtractedValue = {
          ...(llmResult.extractedValue || {}),
          pregnancy_or_postpartum: 'No',
        }
      }
    }

    saveAnswer({
      step,
      text: answerText,
      photoUrl,
      extractedValue: finalExtractedValue,
      value: finalExtractedValue,
      isValid: true,
      attempt: currentAttempt,
      meta: {
        canonicalData: llmResult.canonicalValue || {},
      },
    })

    dispatch(
      setAttemptCounts({
        ...attemptCounts,
        [step.id]: 1,
      }),
    )

    goToNextStep(step.id)
  }

  return {
    handleValidatedStep,
  }
}

export default useChatValidation
